import React from 'react';
import styled, { css } from 'styled-components';
import type { BreakpointOptions } from '@core/images/BreakpointTransforms';
import { gray2 } from '@engage/colors/base';
import type {
  MaybeNumberOrBreakpoints,
  SimpleAvatarProps,
} from '@members/models/SimpleAvatarProps';
import { Image as SimpleImage } from './Image';
import {
  useBadgeFromSpriteSheet,
  BADGE_SIZING_CONSTANT,
  MASK_POSITION_CONSTANT,
  LEADERBOARD_BADGE_SIZING_CONSTANT,
  LEADERBOARD_MASK_POSITION_CONSTANT,
  LEADERBOARD_BADGE_POSITION_CONSTANT,
  BADGE_POSITION_CONSTANT,
} from './member-score/useBadgeFromSpriteSheet';

const SimpleAvatar: React.FC<React.PropsWithChildren<SimpleAvatarProps>> = ({
  url: src,
  size,
  showBadge,
  isSelf,
  badgeUrl,
  isOnLeaderboard = false,
  ...props
}) => {
  const badgeSize =
    getSize(size) *
    (isOnLeaderboard ? LEADERBOARD_BADGE_SIZING_CONSTANT : BADGE_SIZING_CONSTANT);
  const badgeData = useBadgeFromSpriteSheet(showBadge ?? false, isSelf, badgeUrl);

  if (!src) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <NoImage size={getSize(size)} {...props} />;
  }

  const imageProps = {
    breakpointOptions: toAvatarTransforms(size),
    src,
    className: 'fs-exclude',
    ...props,
    size: badgeSize,
    maskUrl: badgeData.maskUrl,
    shouldShowMask: !!badgeData.maskUrl,
    isOnLeaderboard: isOnLeaderboard,
  };

  return badgeData.badgeUrl ? (
    <ImageContainer size={getSize(size)}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StyledSimpleImage {...imageProps} />
      <BadgeIcon
        size={badgeSize}
        url={badgeData.badgeUrl}
        isOnLeaderboard={isOnLeaderboard}
      />
    </ImageContainer>
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledSimpleImage {...imageProps} />
  );
};

const placeholderStyles = css`
  background-color: ${gray2};
  border-radius: 50%;
`;

const NoImage = styled.div<{ size: MaybeNumberOrBreakpoints }>`
  ${placeholderStyles}
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
`;

const StyledSimpleImage = styled(SimpleImage)<{
  size: number;
  maskUrl: string;
  shouldShowMask: boolean;
  isOnLeaderboard: boolean;
}>`
  ${placeholderStyles}
  overflow: hidden;
  ${props =>
    props.shouldShowMask
      ? `
        mask-image: radial-gradient(circle, black 100%, transparent 100%), url(${
          props.maskUrl
        });
        mask-size: 100% 100%, ${props.size}px;
        mask-position: ${
          props.isOnLeaderboard
            ? `${LEADERBOARD_MASK_POSITION_CONSTANT}% ${LEADERBOARD_MASK_POSITION_CONSTANT}%`
            : `${MASK_POSITION_CONSTANT}% ${MASK_POSITION_CONSTANT}%`
        };
        mask-repeat: no-repeat;
        -webkit-mask-composite: source-out;
        mask-composite: subtract;
      `
      : ''}
`;

const ImageContainer = styled.div<{ size: number }>`
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  position: relative;
`;

const BadgeIcon = styled.div<{ size: number; url: string; isOnLeaderboard: boolean }>`
  position: absolute;
  right: ${props =>
    props.isOnLeaderboard
      ? `-${LEADERBOARD_BADGE_POSITION_CONSTANT}%`
      : `-${BADGE_POSITION_CONSTANT}%`};
  bottom: ${props =>
    props.isOnLeaderboard
      ? `-${LEADERBOARD_BADGE_POSITION_CONSTANT}%`
      : `-${BADGE_POSITION_CONSTANT}%`};
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  background-image: url(${props => props.url});
  background-size: cover;
  z-index: 1;
`;

const getSize = (size: MaybeNumberOrBreakpoints) => {
  if (typeof size === 'number') {
    return size;
  } else {
    // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<BreakpointOptionsMap>'.
    return size?.[Object.keys(size)[0]]?.width;
  }
};

const toAvatarTransforms = (size: MaybeNumberOrBreakpoints): BreakpointOptions => {
  if (typeof size === 'number') {
    return {
      mobile: {
        aspectRatio: 1,
        gravity: 'face',
        height: size,
        width: size,
      },
    };
  } else {
    return size as BreakpointOptions;
  }
};

export type { SimpleAvatarProps };

export { SimpleAvatar };
